import { isNativePlatform } from '../platform';

/**
 * The TanStack options every query whose queryFn reads the offline cache must
 * carry, in ONE place - dropping either of them silently disables the cache
 * that queryFn was built around, and nothing downstream notices.
 *
 * A function rather than a module-level const: `isNativePlatform()` has to be
 * evaluated per call, or the platform would be frozen at import time.
 *
 * - `networkMode`: the default 'online' PAUSES the queryFn while offline,
 *   putting the cached read - which lives INSIDE it - permanently out of
 *   reach. 'offlineFirst' is not enough either: it fires the first attempt
 *   but pauses every RETRY while offline (query-core's `canContinue` accepts
 *   only 'always' when the online manager says no), so a cache MISS parks the
 *   query at status 'pending' / fetchStatus 'paused' indefinitely - no error,
 *   no data, and consumers that default an absent list to `[]` then render a
 *   convincing "nothing here". 'always' lets the retries run out so the
 *   honest error reaches the error state the screens already handle.
 * - `refetchOnReconnect`: 'always' rather than true, because a list served
 *   from the cache gets `dataUpdatedAt` stamped NOW and therefore looks fresh
 *   for the whole staleTime - exactly when the data on screen is oldest.
 *
 * Web keeps today's behaviour on both: 'online' parks a disconnected browser
 * rather than erroring where it has no cache to answer with, and reconnect
 * refetching stays off, matching the app-wide default in `App.tsx`.
 */
export function nativeOfflineQueryOptions(): {
  networkMode: 'always' | 'online';
  refetchOnReconnect: 'always' | false;
} {
  const native = isNativePlatform();
  return {
    networkMode: native ? 'always' : 'online',
    refetchOnReconnect: native ? 'always' : false,
  };
}
